import * as Sentry from "@sentry/react";
import { isSessionDead } from "../auth/token.js";
import {
  isExpectedError,
  scrubBreadcrumb,
  scrubEvent,
  scrubValue,
} from "./sentryFilters.js";

/**
 * Initialisiert Sentry für Frontend-Fehlerberichte. Bewusst deaktiviert in der
 * lokalen Entwicklung (import.meta.env.DEV), damit `npm start` nicht bei jedem
 * Reload Events an Sentry schickt. Ohne gesetzten DSN passiert nichts.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  // Bewusst auf MODE statt DEV geprüft: `vite build --mode staging` liefert
  // in dieser Vite-Version DEV=true (DEV spiegelt "mode !== production",
  // nicht "kein echter Build"). `npm start` läuft dagegen ohne --mode-Flag
  // und damit im Standard-Modus "development".
  if (!dsn || import.meta.env.MODE === "development") {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // Ausführliche SDK-Logs in der Browser-Konsole, solange Staging beim
    // Einrichten hilft nachzuvollziehen, ob Events überhaupt gesendet werden.
    debug: import.meta.env.MODE === "staging",
    integrations: [
      Sentry.browserTracingIntegration({
        // Hängt Trace-Header an Requests zur eigenen API, damit Traces
        // später mit einer Backend-Instrumentierung verknüpft werden können.
        tracePropagationTargets: [
          /^\//,
          ...(import.meta.env.VITE_API_URL ? [import.meta.env.VITE_API_URL] : []),
        ],
      }),
      // Bestehende console.warn/error-Aufrufe im Code landen automatisch als
      // strukturierte Sentry-Logs, ohne jede Stelle einzeln umzustellen.
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    // Niedrigere Sample-Rate in Produktion wegen Event-Volumen/Kosten;
    // in Staging voll sampeln, da der Traffic dort ohnehin gering ist.
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.2 : 1.0,
    enableLogs: true,
    // SDK 11 collects all of this by default and ignores sendDefaultPii.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpBodies: [],
      urlQueryParams: false,
    },
    beforeSend(event, hint) {
      if (isExpectedError(hint?.originalException, { sessionDead: isSessionDead() })) {
        return null;
      }
      return scrubEvent(event);
    },
    beforeSendTransaction: scrubEvent,
    // SDK 11 streams spans by default, so they bypass beforeSendTransaction.
    beforeSendSpan: (span) => scrubValue(span),
    beforeSendLog: (log) => scrubValue(log),
    beforeBreadcrumb: scrubBreadcrumb,
  });
}
